// AI Generated Test
import { Component, Input, OnInit } from '@angular/core';
import { LineChart } from '@client/app/modules/charts/models/charts/line';
import MonColors from '@monsido/colors/dist/es6/colors';
import moment from 'moment';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PagePerformanceResultsRepoService } from '@client/app/services/api/page-performance/page-performance-results-repo/page-performance-results-repo.service';
import { HistoryHelperService } from '@client/app/blocks/helpers/history.helper.service';
import { PagePerformanceVersionService } from '@client/app/modules/page-performance/services/page-performance-version/page-performance-version.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { DEFAULTS } from '@monsido/core/constants/defaults.constant';

@Component({
    selector: 'page-performance-page-details-history',
    standalone: false,
    template: `
<div class="page-performance-page-details-performance-history">
    <div class="d-flex align-items-center mb-2 mt-3">
        <span class="fa-2x fa-fw mr-2 text-link"
              aria-hidden="true"
              [ngClass]="['PAGE_PERFORMANCE', 'PERFORMANCE', 'HISTORY'] | monIcons">
        </span>
        <div class="d-flex align-items-center">
            <h2 class="font-weight-300 my-1" translate>
                History
            </h2>
        </div>
    </div>
    <div class="mb-2">
        <date-picker
            [(date)]="date"
            [monDateFormat]="dateFormat"
            (dateChange)="onDateRangeChanges()"
            [options]="datePickerOptions"
            class="inline-block col-41 pl-1">
        </date-picker>
    </div>
    <div class="card panel-monsido-card">
        <div class="card-body">
            <mon-spinner [progress]="progress">
                <div *ngIf="history.length > 0">
                    <mon-chart-line [height]="300"
                                [data]="lineChartData"
                                [labels]="lineChartLabels"
                                [series]="lineChartSettings.series"
                                [colors]="lineChartSettings.colors"
                                [override]="lineChartSettings.override"
                                [options]="lineChartSettings.options">
                    </mon-chart-line>
                    <div class="pagedetails-performance-history-table pt-5">
                        <table-container
                            [progress]="progress"
                            [collection]="rawHistory"
                            (onPageChange)="pageChange($event)"
                            (onPerPageChange)="perPageChange($event)"
                            class="pages-table-wrapper">
                            <div><div *ngFor="let version of versions; let last = last">
                                <table class="table table-hover table-fixed">
                                    <thead>
                                    <tr>
                                        <th class="col-8 col-lg-7" translate>Scanned at</th>
                                        <th class="text-right col-4 col-lg-8" translate>Performance Score</th>
                                        <th class="text-right col-4 col-lg-8" *ngFor="let metric of version.metrics">{{ metric.label | translate }}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr *ngFor="let story of seriesByVersion[version.id]">
                                        <td class="text-v-middle">
                                            <user-date [twoLines]="true"
                                                    [date]="story.scanned_at">
                                            </user-date>
                                        </td>
                                        <td class="text-v-middle text-right">
                                            <page-performance-score-doughnut-chart [score]="story.performance * 100">
                                            </page-performance-score-doughnut-chart>
                                        </td>
                                        <td class="text-v-middle text-right" *ngFor="let metric of version.metrics">
                                            <span>{{formatFieldValue(story[metric.id], metric.id)}}</span>
                                            <span [ngClass]="changesColorClass(story, metric.id)"
                                                *ngIf="getChangePercentageValue(story.id, metric.id) !== 0">
                                                (<span [ngClass]="changesIconClass(story, metric.id)">
                                                </span>
                                                {{getChangePercentageValue(story.id, metric.id)/100 | monNumber:'0.[00]%'}})
                                            </span>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                                <div class="performance-update-indicator" *ngIf="version.label && !last">
                                    <a class="performance-update-indicator-link" [attr.href]="version.link" target="_blank">{{ version.label }}</a>
                                </div>
                            </div></div>
                        </table-container>
                    </div>
                </div>
                <div *ngIf="history.length === 0">
                    <h3 class="text-grey text-center" translate>No performance scans were found</h3>
                </div>
            </mon-spinner>
        </div>
    </div>
</div>
    `,
})
export class PagePerformancePageDetailsHistoryComponent implements OnInit {
    @Input() page: any;
    @Input() profile: any;

    date: any;
    dateFormat: string = DEFAULTS.DATE_FORMAT;
    history: any[] = [];
    versions: any[] = [];
    seriesByVersion: any = {};
    metrics: any[] = [];
    metricsIds: any = {};
    datePickerOptions: any;
    tablePage: number = 1;
    tablePageSize: number = 10;
    progress: any;
    rawHistory: any;
    lineChartSettings: any;
    lineChartData: any[] = [];
    lineChartLabels: any[] = [];

    constructor (
        private translateService: TranslateService,
        private pagePerformanceResultsRepoService: PagePerformanceResultsRepoService,
        private historyHelperService: HistoryHelperService,
        private pagePerformanceVersionService: PagePerformanceVersionService,
        private pipeService: PipeService,
    ) {}

    ngOnInit () {
        this.activate();
    }

    activate () {
        this.date = {
            startDate: moment().subtract(1, 'months'),
            endDate: moment(),
        };
        this.dateFormat = DEFAULTS.DATE_FORMAT;
        this.history = [];
        this.versions = [];
        this.seriesByVersion = {};
        this.metrics = [];
        this.metricsIds = {};
        this.datePickerOptions = {
            maxDate: moment().toDate(),
        };
        this.tablePage = 1;
        this.tablePageSize = 10;
        this.setupChart();
        this.onDateRangeChanges();
    }

    getPerformanceResults () {
        const params: any = {
            page_id: this.page.id,
            profile_id: this.profile.id,
            from: this.date.startDate.format('YYYY-MM-DD'),
            to: this.date.endDate.format('YYYY-MM-DD'),
            page: this.tablePage,
            page_size: this.tablePageSize,
        };
        this.progress = this.pagePerformanceResultsRepoService.getAll(params, undefined).then((res: any) => {
            this.rawHistory = res;
            this.history = res.reverse().map((entry: any, index: number) => {
                entry.id = index;
                return entry;
            });
            const seriesByVersion = this.pagePerformanceVersionService.getSeriesByVersion(this.history, 'version');
            this.versions = seriesByVersion.versions;
            this.seriesByVersion = seriesByVersion.series;

            if (this.versions.length === 1) {
                this.metrics = this.versions[0].metrics;
            } else {
                this.metrics = [];
                this.metricsIds = {};
                for (let i = 0; i < this.versions.length; i++) {
                    const versionMetrics = this.versions[i].metrics;
                    for (let j = 0; j < versionMetrics.length; j++) {
                        const versionMetric = versionMetrics[j];
                        if (!this.metricsIds[versionMetric.id]) {
                            this.metrics.push(versionMetric);
                            this.metricsIds[versionMetric.id] = versionMetric;
                        }
                    }
                }
            }
        });
        return this.progress;
    }

    onDateRangeChanges () {
        this.getPerformanceResults().then(() => {
            this.setupChart();
            this.updateChart();
        });
    }

    pageChange (page: number) {
        this.tablePage = page;
        this.getPerformanceResults().then(() => {
            this.updateChart();
        });
    }

    perPageChange (size: any) {
        this.tablePageSize = size;
        this.getPerformanceResults().then(() => {
            this.updateChart();
        });
    }

    setupChart () {
        this.lineChartSettings = new LineChart();
        this.lineChartData = [];
        this.lineChartLabels = [];
        const availableColors = [
            '#f4151e',
            '#6122ee',
            MonColors['secondary-12'],
            '#00DEDB',
            '#FF9879',
            MonColors['secondary-15'],
            MonColors['secondary-11'],
            MonColors['secondary-16'],
        ];

        const availableOverrides = [
            {
                xAxisID: 'x-axis-0',
                yAxisID: 'y-axis-0',
                type: 'line',
                fill: false,
                lineTension: 0,
                radius: 0,
                borderWidth: 1,
                pointRadius: 3,
                pointHitRadius: 34,
            },
            {
                xAxisID: 'x-axis-0',
                yAxisID: 'y-axis-1',
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                pointHitRadius: 10,
                borderDash: [6, 6],
            },
            {
                xAxisID: 'x-axis-0',
                yAxisID: 'y-axis-1',
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                pointHitRadius: 10,
            },
            {
                xAxisID: 'x-axis-0',
                yAxisID: 'y-axis-1',
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                pointHitRadius: 10,
                borderDash: [4, 4],
            },
            {
                xAxisID: 'x-axis-0',
                yAxisID: 'y-axis-1',
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                pointHitRadius: 10,
            },
            {
                xAxisID: 'x-axis-0',
                yAxisID: 'y-axis-1',
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                pointHitRadius: 10,
                borderDash: [2, 2],
            },
            {
                xAxisID: 'x-axis-0',
                yAxisID: 'y-axis-2',
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
            },
        ];

        const colors: any[] = [];
        const series = [this.translateService.getString('Performance Scans')];
        const overrides = [availableOverrides[0]];

        for (let k = 0; k < this.metrics.length; k++) {
            const metric = this.metrics[k];
            colors.push(availableColors[k]);
            series.push(metric.chartLabel);

            let override: any = availableOverrides[k + 1];
            if (!override) {
                override = {
                    xAxisID: 'x-axis-0',
                    type: 'line',
                    fill: false,
                    lineTension: 0,
                    pointRadius: 0,
                    pointHitRadius: 10,
                    borderDash: [2, 2],
                    yAxisID: 'y-axis-1',
                };
            }
            override.yAxisID = this.getYAxis(metric.unit);
            overrides.push(override);
        }

        this.lineChartSettings.addSeries(series);
        this.lineChartSettings.addColors(colors);
        this.lineChartSettings.addOverrides(overrides);

        this.lineChartSettings.setTooltipSettings({
            intersect: false,
            mode: 'x-axis',
            callbacks: {
                label: (tooltipItem: any, data: any) => {
                    const item = data.datasets[tooltipItem.datasetIndex];
                    const result = item.data[tooltipItem.index].y;
                    const label = item.label;
                    const unit =
                        [this.translateService.getString('Performance Scans'), this.translateService.getString('Cumulative Layout Shift')].indexOf(label) <
                        0
                            ? 'ms'
                            : '';
                    /* jshint ignore:start */
                    const decimals =
                        label === this.translateService.getString('Cumulative Layout Shift')
                            ? this.pipeService.get('monNumber')(result, '0.[00]')
                            : this.pipeService.get('monNumber')(result);
                    /* jshint ignore:end */
                    return item.label + ': ' + decimals + unit;
                },
                title: (tooltipItem: any) => {
                    return moment(tooltipItem[0].xLabel).format('LL LTS');
                },
            },
        });

        this.lineChartSettings.setLegendSettingsOverride({
            display: true,
            position: 'bottom',
            labels: {
                fontSize: 11,
                boxWidth: 10,
            },
        });

        this.lineChartSettings.setScalesSettings({
            yAxes: [
                {
                    id: 'y-axis-0',
                    type: 'linear',
                    position: 'left',
                    ticks: {
                        fontSize: 11,
                        min: 0,
                        max: 100,
                        beginAtZero: true,
                        suggestedMax: 5,
                        maxTicksLimit: 5,
                        callback: (value: any) => {
                            return this.pipeService.get('monNumber')(value, '0,0');
                        },
                    },
                    gridLines: {
                        drawBorder: false,
                    },
                    scaleLabel: {
                        display: true,
                        labelString: this.translateService.getString('Score'),
                        fontSize: 11,
                    },
                },
                {
                    id: 'y-axis-1',
                    type: 'linear',
                    position: 'right',
                    ticks: {
                        fontSize: 11,
                        min: 0,
                        beginAtZero: true,
                        suggestedMax: 5,
                        maxTicksLimit: 5,
                        callback: (value: any) => {
                            return this.pipeService.get('monNumber')(value, '0,0');
                        },
                    },
                    gridLines: {
                        drawBorder: false,
                        display: false,
                    },
                    scaleLabel: {
                        display: true,
                        labelString: this.translateService.getString('Milliseconds'),
                        fontSize: 11,
                    },
                },
                {
                    id: 'y-axis-2',
                    type: 'linear',
                    position: 'right',
                    ticks: {
                        fontSize: 11,
                        min: 0,
                        max: 1,
                        beginAtZero: true,
                        suggestedMax: 1,
                        maxTicksLimit: 2,
                        callback: (value: any) => {
                            return this.pipeService.get('monNumber')(value, '0,00');
                        },
                    },
                    gridLines: {
                        drawBorder: false,
                        display: false,
                    },
                },
            ],
            xAxes: [
                {
                    id: 'x-axis-0',
                    type: 'time',
                    gridLines: {
                        offsetGridLines: true,
                        drawBorder: false,
                        display: false,
                    },
                    ticks: {
                        fontSize: 11,
                        maxTicksLimit: 30,
                    },
                    time: {
                        minUnit: 'day',
                        displayFormats: {
                            millisecond: 'MMM D',
                            second: 'MMM D',
                            minute: 'MMM D',
                            hour: 'MMM D',
                            day: 'MMM D',
                            week: 'MMM D',
                            month: 'MMM D',
                            quarter: 'MMM D',
                            year: 'MMM D',
                        },
                    },
                },
            ],
        });
    }

    getYAxis (unit: string) {
        switch (unit) {
            case 'ms':
                return 'y-axis-1';
            case '':
                return 'y-axis-2';
            default:
                return 'y-axis-0';
        }
    }

    updateChart () {
        this.lineChartData = [[]];
        this.metrics.forEach(() => {
            this.lineChartData.push([]);
        });
        this.history.forEach((entry: any) => {
            this.lineChartData[0].push(this.transformChartData(entry, 'performance'));
            this.metrics.forEach((metric: any, index: number) => {
                this.lineChartData[index + 1].push(this.transformChartData(entry, metric.id));
            });

            this.lineChartLabels.push(new Date(entry.scanned_at).getTime());
        });

        const entriesWithVersionChanged = this.pagePerformanceVersionService.getEntriesWithVersionChanged(this.history, 'version');
        if (entriesWithVersionChanged.length > 0) {
            this.lineChartSettings.options.verticalLines = {
                lines: entriesWithVersionChanged.map((entry: any) => {
                    const versionInfo = this.pagePerformanceVersionService.getVersionInfo(entry.version);
                    return {
                        xAxisID: 'x-axis-0',
                        x: entry.scanned_at,
                        style: {
                            lineDash: [5, 5],
                            color: MonColors['secondary-15'],
                            width: 3,
                        },
                        label: {
                            text: versionInfo ? (versionInfo as any).label : '',
                            color: MonColors['secondary-15'],
                            position: 'right',
                        },
                        data: {
                            link: versionInfo ? (versionInfo as any).link : '',
                        },
                    };
                }),
                timeScale: this.history.map((entry: any) => entry.scanned_at),
            };
        }
    }

    transformChartData (entry: any, field: string) {
        const scoreFields = ['performance'];
        if (scoreFields.indexOf(field) !== -1) {
            return {
                x: moment(entry.scanned_at),
                y: entry[field] * 100,
            };
        } else {
            return {
                x: moment(entry.scanned_at),
                y: entry[field],
            };
        }
    }

    changesColorClass (story: any, field: string) {
        const value = this.getChangeValue(story.id, field);
        return this.historyHelperService.getChangeColorClass(value);
    }

    changesIconClass (story: any, field: string) {
        const value = this.getChangeValue(story.id, field) * -1;
        return this.historyHelperService.getChangeIconClass(value);
    }

    getChangeValue (id: number, field: string) {
        return this.historyHelperService.getChangeValue(id, this.history, [field]);
    }

    getChangePercentageValue (id: number, field: string) {
        return this.historyHelperService.getChangePercentageValue(id, this.history, [field]);
    }

    formatFieldValue (value: any, field: string) {
        if (!value) {
            return 0;
        }
        if (field === 'cumulative_layout_shift') {
            return this.pipeService.get('monNumber')(value, '0.[00]');
        }
        return this.pipeService.get('monNumber')(value, '0,0]') + ' ms';
    }
}
